/**
 * CRM API client
 * Proxy: /api/crm → crm-service:3020
 */

const BASE = '/api/crm';

export type CrmEntityType = 'contacts' | 'companies' | 'deals' | 'activities' | 'tasks';

// ── Types ────────────────────────────────────────────────────────────────────

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  totalPipelineValue?: number;
  page: number;
  pageSize: number;
}

export interface ListParams {
  page?: number;
  pageSize?: 25 | 50 | 100 | 500;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  search?: string;
  filters?: string; // JSON-encoded FilterConfig
  contact_type?: string;
  pipeline_id?: string;
}

export interface Contact {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  mobile?: string;
  company_name?: string;
  company_id?: string;
  job_title?: string;
  lead_source?: string;
  lifecycle_stage: string;
  last_contacted?: string;
  contact_type?: string;
  record_source?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  linkedin_url?: string;
  created_at: string;
  updated_at: string;
  intelligence?: Record<string, unknown>;
}

export interface Company {
  id: string;
  name: string;
  domain?: string;
  phone?: string;
  website?: string;
  industry?: string;
  employee_range?: string;
  type?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  linkedin_url?: string;
  description?: string;
  year_founded?: number;
  annual_revenue?: number;
  parent_company_id?: string;
  account_tier?: string;
  root_company_id?: string;
  created_at: string;
  updated_at: string;
  intelligence?: Record<string, unknown>;
}

export interface HierarchyNode {
  account: Company;
  children: HierarchyNode[];
}

export interface Deal {
  id: string;
  name: string;
  deal_name?: string;
  description?: string;
  amount?: number;
  stage: string;
  stage_id: string;
  pipeline_id: string;
  pipeline_name?: string;
  close_date?: string;
  owner?: string;
  owner_id?: string;
  health_category?: 'on_track' | 'at_risk' | 'off_track' | 'healthy' | 'stalled' | 'critical';
  contact_id?: string;
  company_id?: string;
  primary_contact_id?: string;
  primary_company_id?: string;
  is_closed?: boolean;
  is_won?: boolean;
  lost_reason?: string;
  deal_type?: string;
  priority?: string;
  nda_status?: string;
  forecast_category?: string;
  forecast_probability?: number;
  // New classification fields (Phase 1)
  deal_source_team?: string;
  deal_department?: string;
  deal_revenue_category?: string;
  // New financial fields (Phase 1)
  deal_amount_recurring?: number;
  deal_amount_onetime?: number;
  deal_contract_term_months?: number;
  deal_free_months?: number;
  deal_amount_total?: number;  // server-computed
  deal_arr?: number;           // server-computed (ARR)
  primary_company_name?: string; // server-computed via JOIN (direct company)
  root_company_name?: string;    // server-computed via JOIN (root/HQ company)
  created_at: string;
  updated_at: string;
  intelligence?: Record<string, unknown>;
}

export interface PipelineStage {
  id: string;
  name: string;
  display_order: number;
  probability?: number;
  color?: string;
}

export interface Pipeline {
  id: string;
  name: string;
  stages: PipelineStage[];
  created_at: string;
  updated_at: string;
}

export interface Activity {
  id: string;
  activity_type: 'email' | 'call' | 'meeting' | 'note' | 'task' | 'agent_action';
  entity_type: string;
  entity_id: string;
  title: string;
  body?: string;
  actor_id?: string;
  actor_type: 'human' | 'agent' | 'system';
  occurred_at: string;
  created_at: string;
  metadata?: Record<string, unknown>;
  // Typed columns (migration 145)
  email_subject?: string;
  email_thread_id?: string;
  call_disposition?: string;
  call_duration_seconds?: number;
  call_direction?: string;
  meeting_outcome?: string;
  calendar_event_id?: string;
  note_body?: string;
  is_pinned?: boolean;
}

export interface Task {
  id: string;
  title: string;
  status: 'open' | 'in_progress' | 'completed' | 'cancelled';
  priority?: 'low' | 'medium' | 'high';
  due_date?: string;
  assigned_to?: string;
  related_entity_type?: CrmEntityType;
  related_entity_id?: string;
  created_at: string;
  updated_at: string;
}

export interface ProvisioningRecord {
  id: string;
  company_id: string;
  platform: string;
  method: 'stagehand' | 'manual' | 'api';
  environment: string;
  status: 'active' | 'pending' | 'suspended' | 'deprovisioned';
  provisioned_at: string;
  provisioned_by?: string;
  notes?: string;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface Association {
  id: string;
  from_entity_type: 'contact' | 'company' | 'deal';
  from_entity_id: string;
  to_entity_type: 'contact' | 'company' | 'deal';
  to_entity_id: string;
  association_type: string;
  is_primary: boolean;
  created_at: string;
}

export interface GateEvaluationResult {
  passed: boolean;
  missing_criteria: string[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function buildQuery(params: ListParams): string {
  const q = new URLSearchParams();
  // Backend expects limit/offset, not page/pageSize
  const limit = params.pageSize ?? 25;
  if (params.page != null) {
    q.set('limit', String(limit));
    q.set('offset', String((params.page - 1) * limit));
  } else if (params.pageSize) {
    q.set('limit', String(params.pageSize));
  }
  if (params.sortBy) q.set('sortBy', params.sortBy);
  if (params.sortDir) q.set('sortDir', params.sortDir);
  if (params.search) q.set('search', params.search);
  if (params.filters) q.set('filters', params.filters);
  if (params.contact_type) q.set('contact_type', params.contact_type);
  if (params.pipeline_id) q.set('pipeline_id', params.pipeline_id);
  const str = q.toString();
  return str ? `?${str}` : '';
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    ...init,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const message = err.message || err.error || res.statusText || `HTTP ${res.status}`;
    throw Object.assign(new Error(message), { status: res.status });
  }
  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return undefined as T;
  }
  return res.json() as Promise<T>;
}

// ── Contacts ──────────────────────────────────────────────────────────────────

export const contactsApi = {
  list: (params: ListParams = {}) =>
    request<PaginatedResponse<Contact>>(`${BASE}/contacts${buildQuery(params)}`),

  get: (id: string) =>
    request<Contact>(`${BASE}/contacts/${id}`),

  create: (data: Partial<Contact>) =>
    request<Contact>(`${BASE}/contacts`, { method: 'POST', body: JSON.stringify(data) }),

  update: (id: string, data: Partial<Contact>) =>
    request<Contact>(`${BASE}/contacts/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  delete: (id: string) =>
    request<void>(`${BASE}/contacts/${id}`, { method: 'DELETE' }),

  activities: (id: string) =>
    request<{ data: Activity[] }>(`${BASE}/contacts/${id}/activities`).then(r => r.data),

  associations: (id: string) =>
    request<{ data: Association[] }>(`${BASE}/contacts/${id}/associations`).then(r => r.data),

  bulkUpdate: (ids: string[], updates: Record<string, unknown>) =>
    request<{ updated: number; failed: string[] }>(`${BASE}/contacts/bulk`, {
      method: 'PATCH',
      body: JSON.stringify({ ids, updates }),
    }),

  resolveEmails: (emails: string[]) =>
    request<{ resolved: Record<string, Contact | null> }>(
      `${BASE}/contacts/resolve-emails`,
      { method: 'POST', body: JSON.stringify({ emails }) }
    ),
};

// ── Companies ─────────────────────────────────────────────────────────────────

export const companiesApi = {
  list: (params: ListParams = {}) =>
    request<PaginatedResponse<Company>>(`${BASE}/companies${buildQuery(params)}`),

  get: (id: string) =>
    request<Company>(`${BASE}/companies/${id}`),

  create: (data: Partial<Company>) =>
    request<Company>(`${BASE}/companies`, { method: 'POST', body: JSON.stringify(data) }),

  update: (id: string, data: Partial<Company>) =>
    request<Company>(`${BASE}/companies/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  delete: (id: string) =>
    request<void>(`${BASE}/companies/${id}`, { method: 'DELETE' }),

  activities: (id: string) =>
    request<{ data: Activity[] }>(`${BASE}/companies/${id}/activities`).then(r => r.data),

  associations: (id: string) =>
    request<{ data: Association[] }>(`${BASE}/companies/${id}/associations`).then(r => r.data),

  bulkUpdate: (ids: string[], updates: Record<string, unknown>) =>
    request<{ updated: number; failed: string[] }>(`${BASE}/companies/bulk`, {
      method: 'PATCH',
      body: JSON.stringify({ ids, updates }),
    }),

  children: (id: string) =>
    request<{ data: Company[] }>(`${BASE}/companies/${id}/children`).then(r => r.data),

  ancestors: (id: string) =>
    request<{ data: Company[] }>(`${BASE}/companies/${id}/ancestors`).then(r => r.data),

  hierarchy: (id: string) =>
    request<HierarchyNode>(`${BASE}/companies/${id}/hierarchy`),
};

// ── Deals ─────────────────────────────────────────────────────────────────────

export const dealsApi = {
  list: (params: ListParams = {}) =>
    request<PaginatedResponse<Deal>>(`${BASE}/deals${buildQuery(params)}`),

  get: (id: string) =>
    request<Deal>(`${BASE}/deals/${id}`),

  create: (data: Partial<Deal>) =>
    request<Deal>(`${BASE}/deals`, { method: 'POST', body: JSON.stringify(data) }),

  update: (id: string, data: Partial<Deal>) =>
    request<Deal>(`${BASE}/deals/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  delete: (id: string) =>
    request<void>(`${BASE}/deals/${id}`, { method: 'DELETE' }),

  moveStage: (id: string, stageId: string) =>
    request<{ deal: Deal; gate: GateEvaluationResult }>(`${BASE}/deals/${id}/stage`, {
      method: 'PATCH',
      body: JSON.stringify({ stage_id: stageId }),
    }),

  evaluateGate: (id: string, targetStageId: string) =>
    request<GateEvaluationResult>(`${BASE}/deals/${id}/evaluate-gate`, {
      method: 'POST',
      body: JSON.stringify({ target_stage_id: targetStageId }),
    }),

  activities: (id: string) =>
    request<{ data: Activity[] }>(`${BASE}/deals/${id}/activities`).then(r => r.data),

  associations: (id: string) =>
    request<{ data: Association[] }>(`${BASE}/deals/${id}/associations`).then(r => r.data),

  bulkUpdate: (ids: string[], updates: Record<string, unknown>) =>
    request<{ updated: number; failed: string[] }>(`${BASE}/deals/bulk`, {
      method: 'PATCH',
      body: JSON.stringify({ ids, updates }),
    }),
};

// ── Pipelines ─────────────────────────────────────────────────────────────────

export const pipelinesApi = {
  list: () => request<{ data: Pipeline[] }>(`${BASE}/pipelines`).then(r => r.data),
  get: (id: string) => request<Pipeline>(`${BASE}/pipelines/${id}`),
};

// ── Forecast ──────────────────────────────────────────────────────────────────

export interface ForecastSummary {
  pipeline_arr: number;
  closed_arr: number;
  commit_arr: number;
  best_case_arr: number;
  pipeline_only_arr: number;
}

export interface ForecastWeek {
  week_start: string;
  closed_won: number;
  commit: number;
  best_case: number;
  pipeline: number;
}

export interface ForecastDeal {
  id: string;
  name: string;
  primary_company_name: string | null;
  deal_arr: number;
  close_date: string | null;
  forecast_category: string | null;
  deal_type: string | null;
  owner: string | null;
  is_won: boolean;
  stage: string | null;
}

export interface ForecastData {
  summary: ForecastSummary;
  by_week: ForecastWeek[];
  deals: ForecastDeal[];
}

export const forecastApi = {
  get: (params: { start_date: string; end_date: string }) =>
    request<ForecastData>(`${BASE}/forecast?start_date=${params.start_date}&end_date=${params.end_date}`),
};

// ── Activities ────────────────────────────────────────────────────────────────

export const activitiesApi = {
  list: (params: ListParams = {}) =>
    request<PaginatedResponse<Activity>>(`${BASE}/activities${buildQuery(params)}`),

  create: (data: Partial<Activity>) =>
    request<Activity>(`${BASE}/activities`, { method: 'POST', body: JSON.stringify(data) }),

  togglePin: (id: string, pinned: boolean) =>
    request<Activity>(`${BASE}/activities/${id}/pin`, {
      method: 'PATCH',
      body: JSON.stringify({ pinned }),
    }),

  update: (id: string, data: { body: string; note_body: string }) =>
    request<Activity>(`${BASE}/activities/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
};

// ── Tasks ─────────────────────────────────────────────────────────────────────

export const tasksApi = {
  list: (params: ListParams = {}) =>
    request<PaginatedResponse<Task>>(`${BASE}/tasks${buildQuery(params)}`),

  get: (id: string) =>
    request<Task>(`${BASE}/tasks/${id}`),

  create: (data: Partial<Task>) =>
    request<Task>(`${BASE}/tasks`, { method: 'POST', body: JSON.stringify(data) }),

  update: (id: string, data: Partial<Task>) =>
    request<Task>(`${BASE}/tasks/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  delete: (id: string) =>
    request<void>(`${BASE}/tasks/${id}`, { method: 'DELETE' }),
};

// ── Provisioning ─────────────────────────────────────────────────────────────

export const provisioningApi = {
  list: (companyId: string) =>
    request<{ data: ProvisioningRecord[] }>(`${BASE}/companies/${companyId}/provisioning`).then(r => r.data),

  create: (companyId: string, data: Partial<ProvisioningRecord>) =>
    request<ProvisioningRecord>(`${BASE}/companies/${companyId}/provisioning`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    request<void>(`${BASE}/provisioning/${id}`, { method: 'DELETE' }),
};

// ── Association types ─────────────────────────────────────────────────────────

export interface AssociationTypeDefinition {
  type_key: string;
  label: string;
  from_type: 'contact' | 'company' | 'deal';
  to_type: 'contact' | 'company' | 'deal';
  inverse_label?: string;
}

// ── Associations ─────────────────────────────────────────────────────────────

export const associationsApi = {
  /** Fetch available association types for a given from→to entity pair */
  listTypes: (fromType: string, toType: string) =>
    request<{ data: AssociationTypeDefinition[] }>(
      `${BASE}/associations/types?from_type=${encodeURIComponent(fromType)}&to_type=${encodeURIComponent(toType)}`
    ).then(r => r.data),

  create: (data: {
    from_entity_type: string;
    from_entity_id: string;
    to_entity_type: string;
    to_entity_id: string;
    association_type?: string;
    is_primary?: boolean;
  }) =>
    request<Association>(`${BASE}/associations`, { method: 'POST', body: JSON.stringify(data) }),

  delete: (id: string) =>
    request<void>(`${BASE}/associations/${id}`, { method: 'DELETE' }),
};
